import { Component, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { Settings } from './auth/models/settings';
import { User } from './auth/models/user';
import { AuthService } from './auth/services/auth.service';
import { MsalService } from './auth/sso/msal.service';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppComponent {
  public isAuthenticated$: Observable<User | null>;
  public userSettings$: Observable<Settings | null>;
  /** The status of a failed settings call; the pages are replaced by a message while it is set. */
  public settingsError$: Observable<number | null>;
  /** The login-farmacias portal, where an SSO session can go back to; null in an environment without one. */
  public readonly portalUrl: string | null = environment.sso?.portalUri ?? null;

  constructor(
    private authService: AuthService,
    private msal: MsalService,
    private router: Router
  ) {
    this.isAuthenticated$ = this.authService.currentUser$;
    this.userSettings$ = this.authService.userSettings$;
    this.settingsError$ = this.authService.settingsError$;
  }

  public get isSso(): boolean {
    return this.authService.isSso;
  }

  /**
   * What to tell someone whose settings did not load. Without settings the app has no menu and
   * no pharmacy, so it says so instead of showing an empty page. 403 is the API saying this
   * account has no access to Bidscircle; anything else is a failure worth trying again.
   */
  public settingsErrorMessage(status: number): string {
    if (status === 403) {
      return 'A sua conta não tem acesso à Bidscircle. Se acha que devia ter, peça acesso ao administrador da sua farmácia.';
    }
    return 'Não foi possível carregar os dados da sua conta. Tente novamente dentro de alguns minutos.';
  }

  /**
   * A sign-out the person asked for. A group session is ended at the portal, which owns the
   * sign-out for every application (login-farmacias spec `sso-logout`): ending it here alone
   * would leave the portal still signed in and still offering the applications, and the next
   * sign-out there would show an account picker with nothing in it.
   *
   * AuthService.logout() stays the local teardown a guard or a 401 performs. One application
   * refusing someone is not a reason to sign them out of the others.
   *
   * Routing to the login page belongs here rather than on the button, because only the group
   * sign-out knows not to: the portal navigates away from this app itself, and the login page
   * would otherwise show for the moment MSAL takes to load.
   */
  onLogout(): void {
    if (this.authService.isSso) {
      void this.msal.logout();
      return;
    }

    this.authService.logout();
    this.router.navigate(['/account/login']);
  }
}
